import React from 'react';
import { observer } from 'mobx-react';
import { TafButton } from '@taf-ui/react';

import AppStorage from '../Storage/Storage';
import { isBrand, translate, translateWithLink2 } from '../utils/utils';
import Requests from '../Requests/Requests';
import Labels from '../enums/Labels';
import Brand from '../enums/Brand';

import './SignedHealthDec.css';

import { languageToReadSpeakerLang } from 'ReadSpeakerReader/ReadSpeakerProvider';
import ReadSpeakerReader from 'ReadSpeakerReader';

interface SignedHealthDecState {
  isSignedAvailable: boolean;
  isMedicalReasonAvailable: boolean;
}

declare global {
  interface Navigator {
    msSaveOrOpenBlob: (blobOrBase64: Blob | string, filename: string) => void;
  }
}

class SignedHealthDec extends React.PureComponent<{}, SignedHealthDecState> {
  private readonly storage: AppStorage;

  private intervalId: number | null;

  private headerLabel: Labels;

  private p1Label: Labels;

  private p2Label: Labels;

  private p3Label: Labels;

  private p4Label: Labels;

  private p5Label: Labels;

  private p7Label: Labels;

  constructor(props: {}) {
    super(props);
    this.storage = AppStorage.getInstance();
    this.intervalId = null;
    this.state = {
      isSignedAvailable: false,
      isMedicalReasonAvailable: false,
    };
    if (isBrand(Brand.wefox)) {
      this.headerLabel = Labels.SIGNED_HEALTH_DEC_WEFOX_HEADER;
      this.p1Label = Labels.SIGNED_HEALTH_DEC_P1;
      this.p2Label = Labels.SIGNED_HEALTH_DEC_P2;
      this.p3Label = Labels.SIGNED_HEALTH_DEC_P3;
      this.p4Label = Labels.SIGNED_HEALTH_DEC_P4;
      this.p5Label = Labels.SIGNED_HEALTH_DEC_P5;
      this.p7Label = Labels.SIGNED_HEALTH_DEC_WEFOX_P7;
    } else if (isBrand(Brand.DHMR)) {
      this.headerLabel = Labels.SIGNED_HEALTH_DEC_DHMR_HEADER;
      this.p1Label = Labels.SIGNED_HEALTH_DEC_DHMR_P1;
      this.p2Label = Labels.SIGNED_HEALTH_DEC_DHMR_P2;
      this.p3Label = Labels.SIGNED_HEALTH_DEC_DHMR_P3;
      this.p4Label = Labels.SIGNED_HEALTH_DEC_DHMR_P4;
      this.p5Label = Labels.SIGNED_HEALTH_DEC_DHMR_P5;
      this.p7Label = Labels.SIGNED_HEALTH_DEC_DHMR_P7;
    } else {
      this.headerLabel = Labels.SIGNED_HEALTH_DEC_HEADER;
      this.p1Label = Labels.SIGNED_HEALTH_DEC_P1;
      this.p2Label = Labels.SIGNED_HEALTH_DEC_P2;
      this.p3Label = Labels.SIGNED_HEALTH_DEC_P3;
      this.p4Label = Labels.SIGNED_HEALTH_DEC_P4;
      this.p5Label = Labels.SIGNED_HEALTH_DEC_P5;
      this.p7Label = Labels.SIGNED_HEALTH_DEC_P7;
    }
  }

  componentDidMount(): void {
    this.checkIfSignedDocAvailable();
    this.checkIfMedicalReasonDocAvailable();
  }

  componentWillUnmount(): void {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
    }
  }

  checkIfSignedDocAvailable = (): void => {
    Requests.isSignedDocAvailable()
      .then((response) => {
        const { status } = response;
        if (status === 200) {
          this.setState({
            isSignedAvailable: true,
          });
          if (this.intervalId !== null) {
            window.clearInterval(this.intervalId);
          }
        }
      })
      .catch((error) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (error?.response?.status === 404 && !this.intervalId) {
          setTimeout(() => {
            this.fetchContinuouslyAvailability();
          }, 10);
        }
      });
  };

  checkIfMedicalReasonDocAvailable = (): void => {
    Requests.isMedicalReasonDocAvailable()
      .then((response) => {
        const { status } = response;
        if (status === 200) {
          this.setState({
            isMedicalReasonAvailable: true,
          });
          if (this.intervalId !== null) {
            window.clearInterval(this.intervalId);
          }
        }
      })
      .catch((error) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (error?.response?.status === 404 && !this.intervalId) {
          setTimeout(() => {
            this.fetchContinuouslyAvailability();
          }, 10);
        }
      });
  };

  fetchContinuouslyAvailability = (): void => {
    this.intervalId = window.setInterval(this.checkIfSignedDocAvailable, 30000);
    this.intervalId = window.setInterval(
      this.checkIfMedicalReasonDocAvailable,
      30000,
    );
  };

  downloadPDF = (bytes: string, fileName: string): void => {
    const blob = new Blob([bytes], { type: 'application/pdf' });
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, fileName);
      return;
    }
    const link = document.createElement('a');
    const data = window.URL.createObjectURL(blob);
    link.href = data;
    link.download = fileName;
    link.click();
    setTimeout(() => {
      // For Firefox it is necessary to delay revoking the ObjectURL
      window.URL.revokeObjectURL(data);
    }, 100);
  };

  downloadHealthDeclaration(): void {
    void Requests.downloadSignedDoc().then((response) => {
      if (response.status === 200) {
        const fileName = translate(
          Labels.HEALTH_DECLARATION_FILE_NAME,
          this.storage.getLocale,
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.downloadPDF(response.data, `${fileName}.pdf`);
      }
    });
  }

  downloadMedicalReason(): void {
    void Requests.downloadMedicalReasonDoc().then((response) => {
      if (response.status === 200) {
        const fileName = translate(
          Labels.MEDICAL_REASON_FILE_NAME,
          this.storage.getLocale,
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.downloadPDF(response.data, `${fileName}.pdf`);
      }
    });
  }

  render(): React.ReactElement {
    const { isSignedAvailable, isMedicalReasonAvailable } = this.state;

    const lang = this.storage.getLocale;
    const productSpecificData = this.storage.getProductSpecificInfo;

    return (
      <div
        className="signed-screen"
        lang={languageToReadSpeakerLang(lang)}
        id="signed-health-declaration"
      >
        <ReadSpeakerReader id="signed-health-declaration" />
        <h1>{translate(this.headerLabel, lang)}</h1>
        <p className="info-message">
          {translate(this.p1Label, lang, [productSpecificData.companyName])}
        </p>
        <p className="info-message bold">{translate(this.p2Label, lang)}</p>
        <p className="info-message">{translate(this.p3Label, lang)}</p>
        <p className="info-message bold">{translate(this.p4Label, lang)}</p>
        <p className="info-message">{translate(this.p5Label, lang)}</p>
        <p className="info-message bold">
          {translate(Labels.SIGNED_HEALTH_DEC_P6, lang)}
        </p>
        <p className="info-message">
          <span
            {...translateWithLink2(this.p7Label, lang, [
              productSpecificData.infoEmail,
              productSpecificData.infoEmail,
            ])}
          />
        </p>
        <p>
          {isSignedAvailable ? (
            <TafButton
              id="downloadHealthDec"
              data-testid="downloadHealthDec"
              onClick={this.downloadHealthDeclaration.bind(this)}
              type="button"
              primary={true}
              title={translate(Labels.VIEW_HEALTH_DECLARATION, lang)}
            >
              {translate(Labels.VIEW_HEALTH_DECLARATION, lang)}
            </TafButton>
          ) : (
            <TafButton
              id="pleaseWait"
              type="button"
              primary={true}
              title={translate(Labels.PLEASE_WAIT, lang)}
            >
              {translate(Labels.PLEASE_WAIT, lang)}
            </TafButton>
          )}
        </p>
        <p>
          {isMedicalReasonAvailable && (
            <TafButton
              id="downloadMedicalRea"
              onClick={this.downloadMedicalReason.bind(this)}
              type="button"
              primary={true}
              title={translate(Labels.VIEW_MEDICAL_REASON, lang)}
            >
              {translate(Labels.VIEW_MEDICAL_REASON, lang)}
            </TafButton>
          )}
        </p>
      </div>
    );
  }
}

export default observer(SignedHealthDec);
